/**
 * Billing Type Definitions
 *
 * Types for billing quotas and invoices.
 */

// ============================================================================
// QUOTA TYPES
// ============================================================================

export interface TenantLimits {
  maxUsers: number;
  maxApplications: number;
  maxStorageBytes: number;
  maxApiRequestsPerMonth: number;
  maxDataAgents: number;
  maxEltJobsPerDay: number;
  maxVolumes: number;
  maxSecretsPerApp: number;
  maxManagedSqlDatabases: number;
}

export interface TenantFeatures {
  dataExtraction: boolean;
  customDomains: boolean;
  apiAccess: boolean;
  ssoIntegration: boolean;
  auditLogs: boolean;
  prioritySupport: boolean;
  managedSqlDatabases: boolean;
}

export interface TenantUsage {
  currentUsers: number;
  currentApplications: number;
  currentStorageBytes: number;
  apiRequestsThisMonth: number;
  currentDataAgents: number;
  eltJobsToday: number;
  currentVolumes: number;
  lastUpdated: string;
}

export interface QuotaMetric {
  name: string;
  label: string;
  current: number;
  limit: number;
  percentage: number;
  warning: boolean;
  displayValue: string;
}

export interface QuotasResponse {
  plan: string;
  limits: TenantLimits;
  features: TenantFeatures;
  usage: TenantUsage | null;
  quotaMetrics: QuotaMetric[];
}

// ============================================================================
// INVOICE TYPES
// ============================================================================

export enum InvoiceStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  CANCELLED = "cancelled",
}

export enum InvoicePaymentStatus {
  PENDING = "pending",
  PAID = "paid",
  OVERDUE = "overdue",
  PARTIAL = "partial",
}

export interface PartialPayment {
  paymentId: string;
  amount: number;
  paymentDate: string;
  note?: string;
}

export interface Invoice {
  tenantId: string;
  tenantCode: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  invoiceStatus: InvoiceStatus;
  paymentStatus: InvoicePaymentStatus;
  partialPayments?: PartialPayment[];
  amountPaid?: number;
  description: string;
  originalFilename: string;
  fileSizeBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceListResponse {
  items: Invoice[];
  count: number;
  hasMore: boolean;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  fileName: string;
  expiresAt: string;
  expiresInSeconds: number;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

export const invoicePaymentStatusLabels: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.PENDING]: "Pending",
  [InvoicePaymentStatus.PAID]: "Paid",
  [InvoicePaymentStatus.OVERDUE]: "Overdue",
  [InvoicePaymentStatus.PARTIAL]: "Partial",
};

export const invoicePaymentStatusColors: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800",
  [InvoicePaymentStatus.PAID]: "bg-green-100 text-green-800",
  [InvoicePaymentStatus.OVERDUE]: "bg-red-100 text-red-800",
  [InvoicePaymentStatus.PARTIAL]: "bg-blue-100 text-blue-800",
};

export const planLabels: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

export const planColors: Record<string, string> = {
  free: "bg-gray-100 text-gray-800",
  starter: "bg-blue-100 text-blue-800",
  professional: "bg-purple-100 text-purple-800",
  enterprise: "bg-yellow-100 text-yellow-800",
};
